import React from "react";
import PropTypes from "prop-types";
import { getClient, gqlFromString } from "lib/experience-api";
import { parseGraphQLError } from "lib/experience-api/errorUtils";
import track from "lib/tracking/track";
import {
  GET_LATEST_QUIZ_ATTEMPT,
} from "lib/experience-api/queries";
import {
  NEW_QUIZ_ATTEMPT,
  PROCESS_QUIZ_QUESTION_RESPONSE,
  SUBMIT_QUIZ_QUESTION_RESPONSE,
} from "lib/experience-api/mutations";
import {
  SCREENS,
} from "./constants";
import { startGradingPoller } from "./quizGradingPoller";
import {
  getFirstUnansweredIndex,
  shouldShowIntroForAttempt,
  sortQuestionsByOrder,
} from "./utils";
import LoadingScreen from "./components/LoadingScreen";
import ErrorScreen from "./components/ErrorScreen";
import IntroScreen from "./components/IntroScreen";
import QuestionScreen from "./components/QuestionScreen";
import ResultsScreen from "./components/ResultsScreen";
import InfoAssessmentModal from "./components/InfoAssessmentModal";
import RetakeAssessmentModal from "./components/RetakeAssessmentModal";
import { buildScopeTranslate } from "lib/I18n";

const tErrors = buildScopeTranslate("views.content.agentforce.assessments.errors");
const tNav = buildScopeTranslate("views.content.agentforce.assessments.navigation");
const tCommon = buildScopeTranslate("views.content.agentforce.assessments.common");

const ERROR_TYPES = {
  GRAPHQL: 'graphql_error',
  NETWORK: 'network_error',
  JS_EXCEPTION: 'js_exception',
  VALIDATION: 'validation_error',
};

function classifyError(originalError) {
  if (!originalError) return null;
  if (originalError.graphQLErrors?.length > 0) return ERROR_TYPES.GRAPHQL;
  if (originalError.networkError) return ERROR_TYPES.NETWORK;
  return ERROR_TYPES.JS_EXCEPTION;
}

export default class AgentforceAssessmentApp extends React.Component {
  static propTypes = {
    topicApiName: PropTypes.string.isRequired,
    userTbid: PropTypes.string,
    userEmail: PropTypes.string,
    asyncGradingEnabled: PropTypes.bool,
  };


  state = {
    loading: true,
    error: null,
    attempt: null,
    screen: null,
    currentQuestionIndex: 0,
    answerText: "",
    submitting: false,
    showInfoModal: false,
    showRetakeModal: false,
    sprigAvailable: typeof window !== 'undefined' && typeof window.Sprig === 'function',
  };

  errorHeadingRef = React.createRef();

  introHeadingRef = React.createRef();

  resultsHeadingRef = React.createRef();

  componentDidMount() {
    this.loadAttempt();

    // Event delegation on document for .js-feedback-trigger links in i18n HTML.
    // Using document instead of containerRef because:
    // 1. containerRef is null during loading/error screens (componentDidMount timing)
    // 2. tds-modal renders content in a portal outside the component tree
    this.handleDelegatedFeedbackClick = (e) => {
      const trigger = e.target.closest('.js-feedback-trigger');
      if (trigger) {
        e.preventDefault();
        this.launchFeedback();
      }
    };
    document.addEventListener('click', this.handleDelegatedFeedbackClick);
  }

  componentDidUpdate(prevProps, prevState) {
    const { screen, currentQuestionIndex, attempt, error } = this.state;

    // Update document title on state changes
    if (error && !prevState.error) {
      document.title = tCommon("document_title_error");
    } else if (screen !== prevState.screen || currentQuestionIndex !== prevState.currentQuestionIndex) {
      const topicName = attempt?.topic?.name || tCommon("document_title_suffix");
      const totalQuestions = attempt?.questions?.length || 0;

      switch (screen) {
        case SCREENS.INTRO:
          document.title = tCommon("document_title_intro", { topic: topicName });
          break;
        case SCREENS.QUESTION:
          document.title = tCommon("document_title_question", { current: currentQuestionIndex + 1, total: totalQuestions, topic: topicName });
          break;
        case SCREENS.RESULTS:
          document.title = tCommon("document_title_results", { topic: topicName });
          break;
        default:
          document.title = tCommon("document_title_suffix");
      }
    }

    // Focus the appropriate heading on screen/error transitions.
    let focusTarget;
    if (error && !prevState.error) {
      focusTarget = this.errorHeadingRef;
    } else if (screen !== prevState.screen) {
      const focusTargets = {
        [SCREENS.INTRO]: this.introHeadingRef,
        [SCREENS.RESULTS]: this.resultsHeadingRef,
      };
      focusTarget = focusTargets[screen];
    }

    if (focusTarget) {
      clearTimeout(this._focusTimeout);
      this._focusTimeout = setTimeout(() => {
        const heading = focusTarget.current;
        if (heading && typeof heading.focus === 'function') {
          heading.focus();
        }
      }, 100);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleDelegatedFeedbackClick);
    clearTimeout(this._focusTimeout);
    if (this._cancelPoll) this._cancelPoll();
  }

  showToast = (message, variant = "error") => {
    window.dispatchEvent(
      new CustomEvent("showToast", {
        detail: { heading: message, variant },
      })
    );
  };

  handleGraphQLError = (error, showErrorScreen = false) => {
    const { code, message } = parseGraphQLError(error);

    const errorMessage = code === "INTERNAL_SERVER_ERROR"
      ? tErrors("unexpected_error")
      : message;

    // Track the error event with the raw message, not the localized UI string
    this.trackErrorEvent(message, code, error);

    if (showErrorScreen) {
      this.setState({ error: errorMessage, loading: false });
    } else {
      this.showToast(errorMessage);
    }
  };

  loadAttempt = async () => {
    const { topicApiName } = this.props;
    this.setState({ loading: true, error: null });
    try {
      const result = await getClient().query({
        query: gqlFromString(GET_LATEST_QUIZ_ATTEMPT),
        variables: { topicApiName },
        fetchPolicy: "network-only",
      });
      const attempt = result?.data?.getLatestQuizAttempt;
      // Check if we have a valid attempt with an ID
      if (!attempt || !attempt.quizAttemptId) {
        await this.startNewAttempt();
        return;
      }
      this.setAttemptState(attempt, { showIntro: false });
    } catch (error) {
      this.handleGraphQLError(error, true);
    }
  };

  startNewAttempt = async () => {
    const { topicApiName } = this.props;
    try {
      const result = await getClient().mutate({
        mutation: gqlFromString(NEW_QUIZ_ATTEMPT),
        variables: { input: { topicApiName } },
      });
      const attempt = result?.data?.newQuizAttempt;
      this.setAttemptState(attempt, { showIntro: true });
    } catch (error) {
      this.handleGraphQLError(error, true);
    }
  };

  refreshAttempt = async () => {
    const { topicApiName } = this.props;
    try {
      const result = await getClient().query({
        query: gqlFromString(GET_LATEST_QUIZ_ATTEMPT),
        variables: { topicApiName },
        fetchPolicy: "network-only",
      });
      const attempt = result?.data?.getLatestQuizAttempt;
      if (attempt) {
        const sortedAttempt = sortQuestionsByOrder(attempt);
        this.setState({ attempt: sortedAttempt });
        // Return the refreshed attempt so callers can inspect it synchronously.
        // React 18+ batches setState in async callbacks, so reading this.state
        // right after this call would observe the stale attempt.
        return sortedAttempt;
      }
    } catch (_) {
      // Silently ignore — the original error toast is already shown
    }
    return null;
  };

  setAttemptState = (attempt, { showIntro }) => {
    if (!attempt) {
      this.setState({
        loading: false,
        error: tErrors("no_attempt_data"),
      });
      return;
    }

    attempt = sortQuestionsByOrder(attempt);

    if (attempt.status === "COMPLETED") {
      this.setState({
        loading: false,
        attempt,
        screen: SCREENS.RESULTS,
        submitting: false,
      });
      return;
    }
    if (showIntro || shouldShowIntroForAttempt(attempt)) {
      this.setState({
        loading: false,
        attempt,
        screen: SCREENS.INTRO,
        submitting: false,
      });
      return;
    }
    this.setState({
      loading: false,
      attempt,
      screen: SCREENS.QUESTION,
      currentQuestionIndex: getFirstUnansweredIndex(attempt),
      submitting: false,
    });
  };

  getCurrentQuestion = () => {
    const { attempt, currentQuestionIndex } = this.state;
    if (!attempt?.questions?.length) {
      return null;
    }
    return attempt.questions[currentQuestionIndex];
  };

  handleStartQuiz = () => {
    this.setState((prevState) => ({
      screen: SCREENS.QUESTION,
      currentQuestionIndex: getFirstUnansweredIndex(prevState.attempt),
    }));
  };

  handleAnswerChange = (value) => {
    this.setState({ answerText: value });
  };

  handleSubmitAnswer = async (event) => {
    event.preventDefault();
    const { attempt, answerText, submitting } = this.state;

    // Prevent multiple submissions
    if (submitting) {
      return;
    }

    const currentQuestion = this.getCurrentQuestion();
    if (!attempt || !currentQuestion) {
      return;
    }
    if (!answerText.trim()) {
      const errorMsg = tErrors("enter_response");
      this.trackErrorEvent(errorMsg, null, null, ERROR_TYPES.VALIDATION);
      this.showToast(errorMsg);
      return;
    }
    this.setState({ submitting: true });

    try {
      const result = await getClient().mutate({
        mutation: gqlFromString(PROCESS_QUIZ_QUESTION_RESPONSE),
        variables: {
          input: {
            quizAttemptId: attempt.quizAttemptId,
            quizQuestionId: currentQuestion.quizQuestionId,
            userResponse: answerText,
          },
        },
      });
      const updatedAttempt = result?.data?.processQuizQuestionResponse;
      if (!updatedAttempt) {
        throw new Error("No quiz attempt data returned.");
      }
      const answeredIndex = updatedAttempt.questions.findIndex(
        (question) => question.quizQuestionId === currentQuestion.quizQuestionId
      );
      // Stay on question screen to show feedback, even if completed
      this.setState((prevState) => ({
        attempt: updatedAttempt,
        screen: SCREENS.QUESTION,
        currentQuestionIndex: answeredIndex === -1 ? prevState.currentQuestionIndex : answeredIndex,
        submitting: false,
        answerText: "",
      }));
    } catch (error) {
      this.setState({ submitting: false });
      // Re-fetch the attempt so the client state matches whatever the server
      // persisted. Without this, a page reload could skip the current question
      // if the server partially saved the response before erroring (W-21312953).
      const refreshedAttempt = await this.refreshAttempt();
      // If the refresh reveals the question is fully graded (agentFeedback present),
      // the Org processed the submission successfully despite the timeout — suppress
      // the error toast to avoid confusing the learner with a spurious "Service
      // unavailable" message when their response was actually recorded. (W-21582404)
      // A partial save (response recorded but agentFeedback still null) is not
      // suppressed so the learner knows the submission did not complete normally.
      const questionFullyGraded = refreshedAttempt?.questions?.some(
        (q) => q.quizQuestionId === currentQuestion.quizQuestionId && q.response?.agentFeedback
      );
      if (!questionFullyGraded) {
        this.handleGraphQLError(error);
      }
    }
  };

  handleSubmitAnswerAsync = async (event) => {
    event.preventDefault();
    const { attempt, answerText, submitting } = this.state;

    if (submitting) {
      return;
    }

    const currentQuestion = this.getCurrentQuestion();
    if (!attempt || !currentQuestion) {
      return;
    }
    if (!answerText.trim()) {
      const errorMsg = tErrors("enter_response");
      this.trackErrorEvent(errorMsg, null, null, ERROR_TYPES.VALIDATION);
      this.showToast(errorMsg);
      return;
    }

    this.setState({ submitting: true });

    try {
      const submitResult = await getClient().mutate({
        mutation: gqlFromString(SUBMIT_QUIZ_QUESTION_RESPONSE),
        variables: {
          quizAttemptId: attempt.quizAttemptId,
          quizQuestionId: currentQuestion.quizQuestionId,
          userResponse: answerText,
        },
      });

      const enqueued = submitResult?.data?.submitQuizQuestionResponse;
      if (!enqueued) throw new Error("No submit response returned.");

      const { promise, cancel } = startGradingPoller({
        quizAttemptId: enqueued.quizAttemptId,
        quizQuestionId: enqueued.quizQuestionId,
        topicApiName: attempt.topicApiName,
        onComplete: (gradingResult) => {
          this.setState((prevState) => {
            const mergedQuestions = prevState.attempt.questions.map((q) => {
              const polled = gradingResult.attempt.questions?.find(
                (pq) => pq.quizQuestionId === q.quizQuestionId
              );
              return polled ? { ...q, response: polled.response } : q;
            });
            const sortedAttempt = sortQuestionsByOrder({
              ...prevState.attempt,
              status: gradingResult.attempt.status,
              results: gradingResult.attempt.results,
              questions: mergedQuestions,
            });
            // Use enqueued.quizQuestionId (server-acknowledged ID) rather than
            // currentQuestion.quizQuestionId (client snapshot at submission time).
            const answeredIndex = sortedAttempt.questions.findIndex(
              (q) => q.quizQuestionId === enqueued.quizQuestionId
            );
            return {
              attempt: sortedAttempt,
              screen: SCREENS.QUESTION,
              currentQuestionIndex:
                answeredIndex === -1 ? prevState.currentQuestionIndex : answeredIndex,
              submitting: false,
              answerText: "",
            };
          });
        },
      });

      this._cancelPoll = cancel;
      await promise;
      this._cancelPoll = null;
    } catch (error) {
      this.setState({ submitting: false });
      const refreshedAttempt = await this.refreshAttempt();
      const questionFullyGraded = refreshedAttempt?.questions?.some(
        (q) => q.quizQuestionId === currentQuestion.quizQuestionId && q.response?.agentFeedback
      );
      if (!questionFullyGraded) {
        this.handleGraphQLError(error);
      }
    }
  };

  handleSubmitAnswerDispatch = (event) => {
    if (this.props.asyncGradingEnabled) return this.handleSubmitAnswerAsync(event);
    return this.handleSubmitAnswer(event);
  };

  handleNextQuestion = () => {
    const { attempt, currentQuestionIndex } = this.state;
    if (!attempt?.questions?.length) {
      return;
    }
    // If attempt is completed, go to results screen
    if (attempt.status === "COMPLETED") {
      this.setState({ screen: SCREENS.RESULTS });
      return;
    }
    const nextIndex = attempt.questions.findIndex(
      (question, index) => index > currentQuestionIndex && !question.response
    );
    if (nextIndex === -1) {
      this.setState({ screen: SCREENS.RESULTS });
      return;
    }
    this.setState({ currentQuestionIndex: nextIndex, answerText: "" });
  };

  handleRetake = () => {
    this.handleOpenRetakeModal();
  };

  canRetake = () => {
    const { attempt } = this.state;
    if (!attempt?.results) {
      return false;
    }
    return attempt.results.retakeAllowed;
  };

  handleBackToTrail = () => {
    window.history.back();
  };

  handleOpenInfoModal = () => {
    this.setState({ showInfoModal: true });
  };

  handleCloseInfoModal = () => {
    this.setState({ showInfoModal: false });
  };

  handleOpenRetakeModal = () => {
    this.setState({ showRetakeModal: true });
  };

  handleCloseRetakeModal = () => {
    this.setState({ showRetakeModal: false });
  };

  handleConfirmRetake = () => {
    this.setState({ showRetakeModal: false, loading: true }, this.startNewAttempt);
  };

  handleRetry = () => {
    this.loadAttempt();
  };

  trackErrorEvent = (errorMessage, errorCode, originalError, errorType = classifyError(originalError)) => {
    const { attempt } = this.state;
    const { topicApiName } = this.props;
    const currentQuestion = this.getCurrentQuestion();
    const isCompleted = attempt?.status === 'COMPLETED';

    let httpStatusCode = null;
    if (errorType === ERROR_TYPES.GRAPHQL) {
      httpStatusCode = 200;
    } else if (errorType === ERROR_TYPES.NETWORK) {
      httpStatusCode = originalError.networkError.statusCode
        || originalError.networkError.response?.status
        || null;
    }

    track('enablement_quiz_ui_error_shown', {
      topicApiName: topicApiName,
      errorMessage: errorMessage,
      errorCode: errorCode || null,
      errorType: errorType || null,
      httpResponseStatusCode: httpStatusCode,
      quizAttemptId: attempt?.quizAttemptId || null,
      currentQuizQuestionId: currentQuestion?.quizQuestionId || null,
      status: attempt?.status || null,
      scoreThresholdMet: isCompleted ? (attempt.results?.scoreThresholdMet || false) : null,
      overallScore: isCompleted ? (attempt.results?.overallScore ?? null) : null,
    });
  };

  launchFeedback = () => {
    if (typeof window.Sprig !== 'function') return;

    const { attempt, currentQuestionIndex, answerText, screen } = this.state;
    const { userTbid, userEmail, topicApiName } = this.props;
    const currentQuestion = this.getCurrentQuestion();
    const isCompleted = attempt?.status === 'COMPLETED';

    let status;
    if (screen === SCREENS.INTRO) {
      status = 'not-started';
    } else if (isCompleted) {
      status = 'complete';
    } else {
      status = 'in-progress';
    }

    const attributes = {
      user_tbid: userTbid || null,
      user_email: userEmail || null,
      assessment_id: topicApiName || null,
      assessment_instance_id: attempt?.quizAttemptId || null,
      assessment_scoring: attempt?.topic?.scoreThresholdRequired ? 'required' : 'suggested',
      status,
      question_number: screen === SCREENS.QUESTION && currentQuestion
        ? currentQuestionIndex + 1
        : null,
      question_id: screen === SCREENS.QUESTION && currentQuestion
        ? currentQuestion.quizQuestionId
        : null,
      question_state: screen === SCREENS.QUESTION && currentQuestion
        ? (currentQuestion.response || answerText ? 'answered' : 'not-answered')
        : null,
      score: isCompleted ? attempt.results?.overallScore ?? null : null,
      is_passed: isCompleted ? (attempt.results?.scoreThresholdMet || false) : false,
      is_complete: isCompleted,
    };

    const toSet = {};
    const toRemove = [];
    for (const [key, value] of Object.entries(attributes)) {
      if (value != null) {
        toSet[key] = value;
      } else {
        toRemove.push(key);
      }
    }

    if (toRemove.length > 0) {
      window.Sprig('removeAttributes', toRemove);
    }
    window.Sprig('setAttributes', toSet);
    window.Sprig('track', 'open_feedback_form');

    // Track user activity event for feedback
    track('enablement_quiz_feedback', {
      topicApiName: topicApiName,
      quizAttemptId: attempt?.quizAttemptId || null,
      currentQuizQuestionId: currentQuestion?.quizQuestionId || null,
      status: status || null,
      scoreThresholdMet: isCompleted ? (attempt.results?.scoreThresholdMet || false) : null,
      overallScore: isCompleted ? (attempt.results?.overallScore ?? null) : null,
    });
  };

  render() {
    const { loading, error, screen, attempt, currentQuestionIndex, answerText, submitting, showInfoModal, showRetakeModal } = this.state;

    const renderWithLandmarks = (children) => (
      <>
        <a href="#main-content" className="th-sr-only th-sr-only--focusable" data-testid="skip-link">{tNav("skip_to_content")}</a>
        <main id="main-content">{children}</main>
      </>
    );

    if (loading) {
      return renderWithLandmarks(<LoadingScreen />);
    }

    if (error) {
      return renderWithLandmarks(
        <ErrorScreen
          message={error}
          onRetry={this.handleRetry}
          onBackToTrail={this.handleBackToTrail}
          headingRef={this.errorHeadingRef}
        />
      );
    }

    return renderWithLandmarks(
      <>
        {screen === SCREENS.INTRO && (
          <IntroScreen
            attempt={attempt}
            onStartQuiz={this.handleStartQuiz}
            onBackToTrail={this.handleBackToTrail}
            onInfoClick={this.handleOpenInfoModal}
            onFeedbackClick={this.launchFeedback}
            showFeedbackButton={this.state.sprigAvailable}
            headingRef={this.introHeadingRef}
          />
        )}
        {screen === SCREENS.QUESTION && (
          <QuestionScreen
            attempt={attempt}
            currentQuestion={this.getCurrentQuestion()}
            currentQuestionIndex={currentQuestionIndex}
            answerText={answerText}
            submitting={submitting}
            onAnswerChange={this.handleAnswerChange}
            onSubmitAnswer={this.handleSubmitAnswerDispatch}
            onNextQuestion={this.handleNextQuestion}
            onBackToTrail={this.handleBackToTrail}
            onInfoClick={this.handleOpenInfoModal}
            onFeedbackClick={this.launchFeedback}
            showFeedbackButton={this.state.sprigAvailable}
            headingRef={this.resultsHeadingRef}
          />
        )}
        {screen === SCREENS.RESULTS && (
          <ResultsScreen
            attempt={attempt}
            canRetake={this.canRetake()}
            onRetake={this.handleRetake}
            onBackToTrail={this.handleBackToTrail}
            onInfoClick={this.handleOpenInfoModal}
            onFeedbackClick={this.launchFeedback}
            showFeedbackButton={this.state.sprigAvailable}
          />
        )}
        <InfoAssessmentModal
          open={showInfoModal}
          onClose={this.handleCloseInfoModal}
        />
        <RetakeAssessmentModal
          open={showRetakeModal}
          onClose={this.handleCloseRetakeModal}
          onConfirm={this.handleConfirmRetake}
        />
      </>
    );
  }
}
